/**
 * Validates and sanitizes redirect URLs to prevent open redirect attacks.
 * Only allows relative paths within the application.
 */

/**
 * Checks if URL has dangerous protocol
 */
function hasDangerousProtocol(url: string): boolean {
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:', 'blob:'];
  const lowerUrl = url.toLowerCase();
  return dangerousProtocols.some(protocol => lowerUrl.startsWith(protocol));
}

/**
 * Checks if URL is an absolute URL
 */
function isAbsoluteUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Checks if URL contains suspicious encoded patterns
 */
function hasSuspiciousPatterns(url: string): boolean {
  const suspiciousPatterns = [
    '%00', // null byte
    '%0d',
    '%0D', // carriage return
    '%0a',
    '%0A', // line feed
    '%3c',
    '%3C', // <
    '%3e',
    '%3E', // >
    '%22', // "
    '%27', // '
    '%2e%2e', // encoded ..
    '%252e', // double encoded .
  ];
  const lowerUrl = url.toLowerCase();
  return suspiciousPatterns.some(pattern => lowerUrl.includes(pattern));
}

/**
 * Validates URL structure for safety
 */
function validateUrlStructure(url: string): boolean {
  // Only allow relative paths starting with /
  if (!url.startsWith('/')) return false;

  // Block double slashes after first character
  if (url.substring(1).includes('//')) return false;

  // Block backslashes
  if (url.includes('\\')) return false;

  // Block URLs with credentials
  if (url.includes('@')) return false;

  return true;
}

/**
 * Check if a URL is safe to redirect to.
 * Only allows relative paths starting with "/" and blocks external URLs,
 * javascript: URLs, data: URLs, and other potentially dangerous schemes.
 *
 * @param url - The URL to validate
 * @returns true if the URL is safe, false otherwise
 */
export function isSafeRedirectUrl(url: string | null | undefined): boolean {
  if (!url) return false;

  const trimmedUrl = url.trim();
  if (!trimmedUrl) return false;

  // Run all validation checks
  return (
    !hasDangerousProtocol(trimmedUrl) &&
    !trimmedUrl.startsWith('//') &&
    !isAbsoluteUrl(trimmedUrl) &&
    !hasSuspiciousPatterns(trimmedUrl) &&
    validateUrlStructure(trimmedUrl)
  );
}

/**
 * Sanitizes a redirect URL, returning a safe fallback if the URL is not valid.
 *
 * @param url - The URL to sanitize
 * @param fallback - The fallback URL to use if validation fails (default: '/')
 * @returns A safe URL to redirect to
 */
export function sanitizeRedirectUrl(
  url: string | null | undefined,
  fallback: string = '/',
): string {
  if (isSafeRedirectUrl(url)) {
    return url!;
  }
  return fallback;
}

/**
 * Extracts and validates the redirect parameter from URLSearchParams.
 *
 * @param searchParams - The URLSearchParams object
 * @param fallback - The fallback URL if redirect is invalid (default: '/')
 * @returns A safe redirect URL
 */
export function getSafeRedirectFromParams(
  searchParams: URLSearchParams,
  fallback: string = '/',
): string {
  const redirectUrl = searchParams.get('redirect');
  return sanitizeRedirectUrl(redirectUrl, fallback);
}
